<template>
  <DataPage
    ref="documentsDataPage"
    :fetch-callback="fetchDocuments"
    :model="DocumentModel"
    list
  >
    <template #pageHeader>
      <slot name="pageHeader" />
    </template>

    <template #tableHeaderActions>
      <b-button
        v-can-dynamic:[permission.upload]
        variant="primary"
        size="sm"
        @click="openUploadModal"
      >
        <Icon icon="cloud-upload" size="14" class="mr-2" />
        {{ __('buttons.upload') }}
      </b-button>
    </template>

    <template #iconBlock="{ item }">
      <img :src="item.fileTypeIconName()" :alt="item.name" width="48" />
    </template>

    <template #label="{ item }">
      <RouterLink
        v-if="item.isFolder"
        v-can-dynamic:[permission.view]
        :to="{
          name: documentsRouteName,
          query: { path: getFolderRoutePath(item.name) },
        }"
      >
        {{ item.name }}
      </RouterLink>
      <a
        v-else
        v-can-dynamic:[permission.download]
        href="#"
        @click.prevent="preview(item)"
      >
        {{ item.name }}
      </a>
    </template>

    <template #badge="{ item }">
      <Badge v-if="item.getFileType()" variant="soft-secondary" class="ml-3">
        {{ item.getFileType() }}
      </Badge>
    </template>

    <template #description="{ item }">
      <span class="text-muted">
        {{ item.description() }}
      </span>
    </template>

    <template #actionButtons="{ item }">
      <Badge v-if="item.type" variant="soft-info" class="ml-3">
        {{ item.type.name }}
      </Badge>

      <ActionPopover
        :id="item.id"
        v-can-any="[permission.download, 'documents.destroy']"
      >
        <b-button
          v-if="!item.isFolder"
          v-can-dynamic:[permission.download]
          class="dropdown-item"
          size="sm"
          @click="preview(item)"
        >
          <Icon icon="eye" class="mr-2" size="14" />
          Preview
        </b-button>

        <b-button
          v-if="!item.isFolder"
          v-can-dynamic:[permission.download]
          class="dropdown-item"
          size="sm"
          @click="download(item)"
        >
          <Icon icon="cloud-download" class="mr-2" size="14" />
          {{ __('buttons.download') }}
        </b-button>

        <b-button
          v-if="!item.isFolder"
          v-can:documents.destroy
          class="dropdown-item"
          size="sm"
          @click="remove(item.id)"
        >
          <Icon icon="trash" class="mr-2" size="14" />
          {{ __('buttons.delete') }}
        </b-button>
      </ActionPopover>
    </template>
  </DataPage>
</template>

<script>
import DataPage from '@/General/Tables/DataPage.vue'
import DocumentModel from '@/Modules/Document/DocumentModel.js'
import UploadModal from '@/Modules/Document/Components/OneDrive/UploadModal.vue'

export default {
  name: 'DocumentList',

  components: { DataPage },

  props: {
    documentService: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      DocumentModel: DocumentModel,
    }
  },

  computed: {
    documentsRouteName() {
      return this.$route.name
    },
    permission() {
      return {
        upload: `${this.documentService.resource}.documents.upload`,
        view: `${this.documentService.resource}.documents.view`,
        download: `${this.documentService.resource}.documents.view`,
      }
    },
  },

  created() {
    this.$watch('$route.params', this.handleRouteChange)
  },

  methods: {
    handleRouteChange() {
      this.$refs.documentsDataPage.refreshTable()
    },

    getRouteIdKey() {
      switch (this.documentService.resource) {
        case 'entities':
          return 'entityId'
        case 'quotes':
          return 'quoteId'
        case 'clients':
          return 'clientId'
        default:
          return ''
      }
    },

    fetchDocuments(parameters) {
      const { path } = this.$route.query
      const id = this.$route.params[this.getRouteIdKey()]
      if (path) {
        return this.documentService.list(id, {
          ...parameters,
          filters: `?path=${path}`,
        })
      }
      return this.documentService.list(id, parameters)
    },

    download(item) {
      this.documentService.show(
        this.$route.params[this.getRouteIdKey()],
        item.id,
        item.fileName
      )
    },

    preview(item) {
      this.documentService.download(
        this.$route.params[this.getRouteIdKey()],
        item.id,
      ).then((url) => {
        if (url) {
          window.open(url, '_blank');
        }
      }).catch((error) => console.error(error))
    },

    remove(id) {
      this.$showConfirmationModal(
        (this.__(`documents.confirmation-delete`)),
        () => {
          this.documentService
            .delete(this.$route.params[this.getRouteIdKey()], id)
            .then(() => {
              this.$successToast(this.__(`documents.delete-success`))
              this.$refs.documentsDataPage.refreshTable()
            })
        }
      )
    },

    openUploadModal() {
      this.showModal({
        component: UploadModal,
        label: this.__(`documents.upload`),
        icon: 'cloud-upload',
        class: 'modal-sm',
        meta: {
          documentFolderId: this.$route.params.folderId,
          uploadCallback: (formData) =>
            this.documentService.create(
              this.$route.params[this.getRouteIdKey()],
              formData
            ),
        },
        onSuccess: () => {
          this.$refs.documentsDataPage.refreshTable()
        },
      })
    },

    getFolderRoutePath(name) {
      const routeQueryPath = this.$route.query.path
        ? `${this.$route.query.path}/`
        : ''
      return `${routeQueryPath}${name}`
    },
  },
}
</script>
